import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { products } from "../../utils/productsData";
import Footer from "../../components/Footer";
import ScrollToTopButton from "../../components/ScrollTop";
import Testimonials from "../../components/Testimonials";
import WhatsAppMsg from "../../components/WhatsApp";
import FAQ from "../../components/FAQ";
import { faqData } from "../../utils/productFAQ";
import Header from "../../components/Header";
import SEO from "../../components/SEO";
import { Link } from "react-router-dom";
import { IoSearchOutline, IoArrowForward } from "react-icons/io5";
import { FaArrowRightLong } from "react-icons/fa6";

const Products = () => {
  // Single selected category & size
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSize, setSelectedSize] = useState("");

  // For searching by product name
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Rotating images for dynamic section
  const rotatingImages = [
    "https://content.qeeb.in/coltec/products/stunning-4k-clarity.webp",
    "https://content.qeeb.in/coltec/products/real-time-engagement.webp",
    "https://content.qeeb.in/coltec/products/seamless-touch-interaction.webp",
  ];
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Helper function to generate dynamic alt text based on image URL
  const getAltText = (url) => {
    const fileName = url.substring(url.lastIndexOf("/") + 1);
    return fileName.replace(".webp", "").replace(/-/g, " ");
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % rotatingImages.length
      );
    }, 6000);
    return () => clearInterval(interval);
  }, [rotatingImages.length]);

  // Combine all products from every category into one array
  const allProducts = Object.values(products).flat();

  // Updated helper function to extract size from product name.
  // It now supports names like "COLTEC Rise | 65-Inch Smart Interactive Flat Panel" or "65 inch" etc.
  const extractSize = (name) => {
    const match = name.match(/(\d+)\s*-?\s*(Inch|inches|in)\b/i);
    return match ? `${match[1]} Inch` : null;
  };

  // Compute distinct sizes from all products.
  const sizesSet = new Set();
  allProducts.forEach((product) => {
    const size = extractSize(product.name);
    if (size) sizesSet.add(size);
  });
  const sizeOptions = Array.from(sizesSet);

  // Define category options (excluding "All Products" as empty selection means all).
  const categoryOptions = [
    "Interactive Flat Panel",
    "Active LED Video Wall",
    "Commercial Display",
    "Room Scheduler",
    "Kiosk",
    "All-in-One Touch Monitor",
    "Tablet",
    "Video Wall",
  ];

  const testimonials = [
    {
      image:
        "https://content.qeeb.in/coltec/testimonials/global-sainik-academy.jpg",
      name: "Sharan Sikenpore",
      altImage: "Coltec Customer Global Sainik Academy",
      designation: "Founder Chairman & CEO",
      headline: "Excellent clarity and performance",
      description:
        "“The interactive flat panels from COLTEC have transformed our classrooms! The crystal-clear display and responsive touch technology make lessons more engaging for students. We’re very happy with the performance and reliability.”",
    },
    {
      image: "https://content.qeeb.in/coltec/testimonials/shaheen-group.jpg",
      name: "Mr Qursheed Ahmed",
      altImage: "Coltec Customer Shaheen Group",
      designation: "Academic Councellor",
      headline: "Great investment for our school",
      description:
        "“Implementing COLTEC’s interactive panels has enhanced our teaching experience. The easy-to-use interface and built-in educational tools have made learning more dynamic and interactive.”",
    },
    {
      image: "https://content.qeeb.in/coltec/testimonials/gaudium-logo.png",
      name: "Sudeshna Chatterjee",
      altImage: "Coltec Customer Gaudium School",
      designation: "Head of School",
      headline: "User-friendly and efficient",
      description:
        "“We’re thrilled with the versatility of COLTEC’s smart panels. They’ve integrated perfectly into our boardrooms, providing a powerful tool for presentations and brainstorming sessions.”",
    },
    {
      image: "https://content.qeeb.in/coltec/testimonials/iit-madras-logo.png",
      name: "Vikas Sitraman",
      altImage: "Coltec Customer IIT Madras",
      designation: "IIT Madras - Academic Councellor",
      headline: "Amazing display for corporate use",
      description:
        "“Our classrooms are more interactive and vibrant thanks to COLTEC’s IFPDs. The durability and advanced features like multi-touch and integrated content have made a significant impact on student engagement.”",
    },
    {
      image: "https://content.qeeb.in/coltec/testimonials/berkadia.jpg",
      name: "Raj Kumar",
      altImage: "Coltec Customer Berkadia",
      designation: "AV Head",
      headline: "Transformed our training sessions",
      description:
        "“COLTEC’s interactive displays are a cornerstone of our new training facilities. The high-quality visuals and intuitive touch controls have enhanced our employee training programs significantly. We are very pleased with the results.”",
    },
    {
      image: "https://content.qeeb.in/coltec/testimonials/spring-filed.jpg",
      name: "Akash Awasti",
      altImage: "Coltec Customer Spring Filed",
      designation: "Vice Principal",
      headline: "Innovative and reliable technology",
      description:
        "“Integrating COLTEC’s interactive panels has truly modernized our classrooms. The high-definition display and robust interactive features have significantly boosted student engagement. Teachers find it intuitive to use, and students are more excited to participate in lessons. It’s been a fantastic investment for our school.”",
    },
    {
      image: "https://content.qeeb.in/coltec/testimonials/edify-education.png",
      name: "Kishore Pandit",
      altImage: "Coltec Customer Edify Education",
      designation: "Head of School",
      headline: "Superior quality and outstanding support",
      description:
        "“COLTEC’s interactive flat panels have transformed our teaching methods. The seamless integration of multimedia content and the responsive touch interface make lessons more interactive and enjoyable. Our teachers and students are thrilled with the new technology, which has made learning much more dynamic and effective.”",
    },
    {
      image:
        "https://content.qeeb.in/coltec/testimonials/the-dollar-business.png",
      name: "Ashok Tripathi",
      altImage: "Coltec Customer The Dollar Business",
      designation: "IT Head",
      headline: "A game changer for interactive learning",
      description:
        "“We’re thrilled with the versatility of COLTEC’s smart panels. They’ve integrated perfectly into our boardrooms, providing a powerful tool for presentations and brainstorming sessions.”",
    },
  ];

  // Static breadcrumbs (positions 1 to 7)
  const staticBreadcrumbs = [
    { position: 1, name: "Home", url: "https://coltec-global.com" },
    {
      position: 2,
      name: "Products",
      url: "https://coltec-global.com/shop",
    },
    {
      position: 3,
      name: "COLTEC Evoke Series",
      url: "https://coltec-global.com/products/interactive-flat-panel/evoke-series",
    },
    {
      position: 4,
      name: "COLTEC Rise Series",
      url: "https://coltec-global.com/products/interactive-flat-panel/rise-series",
    },
    {
      position: 5,
      name: "COLTEC Inspire Series",
      url: "https://coltec-global.com/products/interactive-flat-panel/inspire-series",
    },
    {
      position: 6,
      name: "COLTEC Inertia Series",
      url: "https://coltec-global.com/products/active-led/inertia-series",
    },
    {
      position: 7,
      name: "COLTEC Vista Series",
      url: "https://coltec-global.com/products/commercial-display/vista-series",
    },
  ];

  // Dynamic generation for positions 8+ from productsData
  const interactiveFlatPanelProducts = [
    ...(products["Interactive Flat Panel"] || []),
    ...(products["Active LED Video Wall"] || []),
    ...(products["Commercial Display"] || []),
  ];

  // Map each variant to a breadcrumb item
  const dynamicBreadcrumbs = interactiveFlatPanelProducts.map(
    (product, index) => ({
      position: staticBreadcrumbs.length + index + 1,
      name: product.name, // e.g., "COLTEC Rise | 65 Inch Smart Interactive Flat Panel"
      url: `https://coltec-global.com/product-details/${product.slug}`,
    })
  );

  // Combine static and dynamic breadcrumb items
  const allBreadcrumbs = [...staticBreadcrumbs, ...dynamicBreadcrumbs];

  // Build the JSON‑LD BreadcrumbList
  const breadcrumbList = {
    "@type": "BreadcrumbList",
    itemListElement: allBreadcrumbs.map((bc) => ({
      "@type": "ListItem",
      position: bc.position,
      name: bc.name,
      item: bc.url,
    })),
  };

  // Handle changes for category & size dropdowns
  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const handleSizeChange = (e) => {
    setSelectedSize(e.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    // Only update the "searchTerm" when button is clicked
    setSearchTerm(searchInput.trim());
  };

  // 1) Filter by category
  let productsAfterCategoryFilter = allProducts;
  if (selectedCategory) {
    // If a category is selected, take only products from that category array
    // or filter from the big array if each product has a `category` property.
    // Since your data is structured by `products[category]`, we can do:
    productsAfterCategoryFilter = products[selectedCategory] || [];
  }

  // 2) Filter by size
  let productsAfterSizeFilter = productsAfterCategoryFilter.filter(
    (product) => {
      if (!selectedSize) return true; // no size selected => keep all
      const productSize = extractSize(product.name);
      return productSize === selectedSize;
    }
  );

  // 3) Filter by search term
  const finalFilteredProducts = productsAfterSizeFilter.filter((product) => {
    if (!searchTerm) return true;
    return product.name.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <>
      <div className="about-us-page">
        <SEO
          title="Discover Coltec’s Interactive Flat Panels & Solutions"
          description="Explore Coltec's range of interactive flat panels and collaboration solutions, designed to enhance engagement and productivity in both education and business environments."
          keywords="interactive flat panels,touchscreen displays,digital whiteboards,collaboration solutions,educational technology, corporate training displays, zero bonding touch technology,multi-touch panels,remote learning solutions,hybrid classrooms,digital signage,interactive display supplier in India,best interactive flat panel for schools,business collaboration tools,high-definition interactive displays,teaching software solutions,anti-glare interactive screens, smart classroom technology"
          ogTitle="Coltec Global | Innovative Interactive Displays & Collaboration Solutions"
          ogDescription="Discover the full range of Coltec interactive flat panels, designed to revolutionize presentations, learning, and collaboration for modern classrooms and workplaces."
          ogImage="https://content.qeeb.in/coltec/products/banner-product-three.webp"
          ogUrl="https://coltec-global.com/shop"
          canonicalUrl="https://coltec-global.com/shop"
          ogImageAlt="Coltec Global Product Lineup of Interactive Flat Panels"
          ogSiteName="Coltec Global"
          ogType="website"
          twitterSite="https://x.com/ColtecGlobal" // Twitter username associated with the site
          twitterTitle="Discover Coltec’s Interactive Flat Panels & Solutions"
          twitterDescription="Explore Coltec's range of interactive flat panels and collaboration solutions, designed to enhance engagement and productivity in both education and business environments."
          twitterImage="https://content.qeeb.in/coltec/products/banner-product-three.webp"
          twitterCard="summary" // Using a large image card for enhanced visual display
          geoRegion="IN"
        />
        {/* JSON‑LD Structured Data with Publisher & Address Details */}
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(
              {
                "@context": "https://schema.org",
                "@graph": [
                  // Organization details
                  {
                    "@type": "Organization",
                    "@id": "https://coltec-global.com/#organization",
                    name: "COLTEC Global",
                    // Rating details
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.8",
                      reviewCount: "350",
                    },
                    description:
                      "COLTEC Global is a leading manufacturer of Visual Interactive Display Technology, delivering innovative solutions for Education, Corporate, and Retail applications.",
                    url: "https://coltec-global.com",
                    logo: {
                      "@type": "ImageObject",
                      url: "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    },
                    address: [
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                        addressLocality: "Hyderabad",
                        addressRegion: "Telangana",
                        postalCode: "500030",
                        addressCountry: "IN",
                      },
                      {
                        "@type": "PostalAddress",
                        streetAddress:
                          "COLTEC GLOBAL #41-14-8/2, Opp Mouli Towers, Near Jyothi Convention Hall,Benz circle",
                        addressLocality: "Vijayawada",
                        addressRegion: "Andhra Pradesh",
                        postalCode: "520 010",
                        addressCountry: "IN",
                      },
                    ],
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                    email: "sales@coltec-global.com",
                    contactPoint: [
                      {
                        "@type": "ContactPoint",
                        telephone: "+91 1800 309 8822",
                        contactType: "customer support",
                      },
                    ],
                  },
                  // WebSite details
                  {
                    "@type": "WebSite",
                    "@id": "https://coltec-global.com/#website",
                    url: "https://coltec-global.com",
                    name: "COLTEC Global",
                    publisher: {
                      "@id": "https://coltec-global.com/#organization",
                    },
                    potentialAction: {
                      "@type": "SearchAction",
                      target:
                        "https://coltec-global.com/shop?s={search_term_string}",
                      "query-input": "required name=search_term_string",
                    },
                    inLanguage: "en",
                  },
                  // Updated Product details with additional properties
                  {
                    "@type": "Product",
                    name: "Discover Coltec’s Interactive Flat Panels & Solutions",
                    url: "https://coltec-global.com/shop",
                    description:
                      "Explore Coltec's range of interactive flat panels and collaboration solutions, designed to enhance engagement and productivity in both education and business environments.",
                    brand: {
                      "@type": "Brand",
                      name: "COLTEC Global Products",
                      url: "https://coltec-global.com/shop",
                      image:
                        "https://content.qeeb.in/coltec/products/banner-product-three.webp",
                    },
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: "4.5",
                      reviewCount: "500",
                    },
                    additionalProperty: finalFilteredProducts.map(
                      (product) => ({
                        "@type": "PropertyValue",
                        name: product.name,
                        image: product.imageUrl,
                        value: product.description,
                      })
                    ),
                    // Define offers as a top-level property (an array if there are multiple offers)
                    offers: finalFilteredProducts.map((product) => ({
                      "@type": "AggregateOffer",
                      lowPrice: product.price,
                      highPrice: product.originalPrice,
                      priceCurrency: "INR",
                      availability: "https://schema.org/InStock",
                    })),
                  },
                  // Breadcrumb for Home page
                  breadcrumbList,
                  // Dynamically generated customer testimonials as Review objects
                  ...testimonials.map((t) => ({
                    "@type": "Review",
                    headline: t.headline,

                    author: {
                      "@type": "Person",
                      name: t.name,
                    },
                    reviewBody: t.description,
                    reviewRating: {
                      "@type": "Rating",
                      ratingValue: "4.5",
                      bestRating: "5",
                      worstRating: "1",
                    },
                    datePublished: "2024-02-12",
                    // Optionally, add more properties such as a publication date or reviewRating if available
                    publisher: {
                      "@type": "Organization",
                      name: "COLTEC Global",
                    },
                  })),
                  {
                    "@type": "LocalBusiness",
                    "@id": "https://coltec-global.com/#localbusiness",
                    name: "COLTEC Global",
                    url: "https://coltec-global.com",
                    image:
                      "https://content.qeeb.in/coltec/coltec-logo-black.svg",
                    address: {
                      "@type": "PostalAddress",
                      streetAddress:
                        "37, Survey No 75/A, Gagan Pahad Village, Raghavendra Nagar",
                      addressLocality: "Hyderabad",
                      addressRegion: "Telangana",
                      postalCode: "500030",
                      addressCountry: "IN",
                    },
                    telephone: "+91 1800 309 8822",
                    openingHours: "Mon-Sat 09:00-6:00",
                    geo: {
                      "@type": "GeoCoordinates",
                      latitude: "17.3297791",
                      longitude: "78.4381149",
                    },
                    sameAs: [
                      "https://www.facebook.com/profile.php?id=61570213181020",
                      "https://x.com/ColtecGlobal",
                      "https://www.linkedin.com/company/coltec-global/",
                      "https://www.youtube.com/@coltecglobal5339",
                      "https://www.google.com/maps/place/COLTEC+GLOBAL+INDIA/@17.3297791,78.4381149,17z/data=!3m1!4b1!4m6!3m5!1s0x3bcbbd8bb97fefcd:0x2536a4697f2f1423!8m2!3d17.3297791!4d78.4406898!16s%2Fg%2F11w1dth9rd?entry=ttu&g_ep=EgoyMDI1MDIyNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D",
                    ],
                  },
                ],
              },
              null,
              2
            )}
          </script>
        </Helmet>

        <Header />

        <div className="px-6 lg:px-8 lg:pb-14 lg:pt-20 pt-16 pb-10 max-w-7xl m-auto">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-black md:text-3xl text-xl font-semibold font-['Poppins']">
              Shop COLTEC Products
            </h2>
            <div className="md:hidden block flex">
              {/* Filter by Product (Category) */}
              <select
                className="border border-gray-300 px-3 py-2 text-xs rounded-md"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="">Filter by Product</option>
                {categoryOptions.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              {/* Filter by Size */}
              <select
                className="border border-gray-300 px-3 ml-2 py-2 text-xs rounded-md md:w-36 w-[115px]"
                value={selectedSize}
                onChange={handleSizeChange}
              >
                <option value="">Filter by Size</option>
                {sizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Search Box + Button */}
            <div className="flex items-center">
              <div className="relative">
                <IoSearchOutline className="absolute left-3 text-[#737373] top-2.5" />
                <input
                  type="text"
                  placeholder="Search Product Name"
                  className="border border-gray-300 rounded-l-md pl-8 md:text-sm text-xs pr-3 py-2"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <button
                onClick={handleSearch}
                className="flex items-center bg-[#3b82f6] hover:bg-[#3b83f6f5] md:text-sm text-xs font-normal leading-normal text-white px-5 md:px-4 py-2 rounded-r"
              >
                Search <FaArrowRightLong className="ml-2" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-12 md:mt-6 mt-2 lg:gap-8 gap-4">
            {/* Left Sidebar: Categories */}
            <div className="md:col-span-3 col-span-full">
              <div className="md:block hidden">
                {/* Filter by Product (Category) */}
                <select
                  className="border mb-4 w-full border-gray-300 px-3 py-2 text-sm rounded-md"
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                >
                  <option value="">Filter by Product</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>

                {/* Filter by Size */}
                <select
                  className="border mb-4 w-full border-gray-300 px-3 py-2 text-sm rounded-md"
                  value={selectedSize}
                  onChange={handleSizeChange}
                >
                  <option value="">Filter by Size</option>
                  {sizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
              <div className="hidden md:block">
                <img
                  alt="Shop Banner"
                  src="https://content.qeeb.in/coltec/products/shop-discount-banner.webp"
                  className="w-full border rounded-lg"
                />
              </div>
            </div>

            {/* Right Side: Products */}
            <div className="md:col-span-9 col-span-full">
              <div className="grid 2xl:grid-cols-3 lg:grid-cols-3 md:grid-cols-2 grid-cols-2 md:gap-6 gap-3">
                {finalFilteredProducts.length > 0 ? (
                  finalFilteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="md:p-4 p-2 bg-white rounded-2xl shadow-[0px_4px_16px_0px_rgba(51,51,51,0.10)] border-2 border-stone-300"
                    >
                      <Link
                        to={`/product-details/${product.slug}`}
                        target="_blank"
                      >
                        <div className="flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={product.imageUrl}
                            alt={product.name}
                            className="rounded-lg hover:scale-105 transition-all duration-500 object-contain"
                          />
                        </div>
                        <div className="md:mt-3 mt-2">
                          <h3 className="md:text-base text-[11px] font-medium">
                            {product.name}
                          </h3>
                          <div className="flex justify-between items-center">
                            <p className="md:text-base text-xs mt-1 font-semibold text-gray-800">
                              ₹{product.price}{" "}
                              <span className="line-through font-normal md:text-sm text-[10px] ml-1 md:ml-2 text-gray-400">
                                ₹{product.originalPrice}
                              </span>
                            </p>
                            <IoArrowForward className="md:text-[22px] text-sm hover:text-blue-600 hover:translate-x-1 transition-all ease-linear duration-200" />
                          </div>
                        </div>
                      </Link>
                    </div>
                  ))
                ) : (
                  <div className="col-span-full text-center text-gray-600">
                    No products found
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-[#fafafa]">
          <div className="px-6 lg:px-8 lg:py-14 py-10 max-w-7xl m-auto relative">
            <Testimonials testimonials={testimonials} />
          </div>
        </div>
        {/* New Rotating Image Section */}
        <div>
          <img
            src={rotatingImages[currentImageIndex]}
            alt={getAltText(rotatingImages[currentImageIndex])}
            className="w-full"
          />
        </div>
        <div className="px-6 lg:px-8 lg:py-14 py-10 max-w-7xl m-auto">
          <FAQ data={faqData} />
        </div>
        <Footer />
        <ScrollToTopButton />
        <WhatsAppMsg />
      </div>
    </>
  );
};

export default Products;
